import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../components/ui/Toast';
import { FaCheckCircle, FaDownload, FaFileInvoice, FaHome, FaSpinner, FaEye, FaTimes, FaShoppingCart } from 'react-icons/fa';
import axios from 'axios';
import { useTheme } from '../../context/SocketContext';

const PaymentSuccess = () => {
  const { currentThemeColors } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const paymentId = searchParams.get('payment_id');
  const orderId = searchParams.get('order_id');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  useEffect(() => {
    if (showPreview) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPreview]);

  // Redirect if no payment params
  useEffect(() => {
    if (!paymentId || !orderId) {
      toast.error('Invalid payment confirmation');
      navigate('/pricing');
    }
  }, [paymentId, orderId, navigate]);

  // Fetch invoice in background (non-blocking)
  useEffect(() => {
    if (!paymentId || !orderId) return;

    const fetchInvoice = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/user`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const invoices = response.data.user.invoices || [];
        if (invoices.length > 0) {
          const latestInvoice = invoices[invoices.length - 1];
          const fullInvoiceUrl = `${process.env.REACT_APP_API_BASE_URL}${latestInvoice.fileUrl}`;
          
          const pdfResponse = await axios.get(fullInvoiceUrl, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });
          
          const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          setInvoiceUrl(blobUrl);
        } else if (retryCountRef.current < maxRetries) {
          // Invoice not ready yet, retry after a short delay
          retryCountRef.current += 1;
          setTimeout(fetchInvoice, 2000);
          return;
        }
      } catch (error) {
        console.error('Error fetching invoice:', error);
        if (retryCountRef.current < maxRetries) {
          retryCountRef.current += 1;
          setTimeout(fetchInvoice, 2000);
          return;
        }
      }
      setInvoiceLoading(false);
    };

    // Start fetching invoice after a brief delay to let the backend finish generating it
    const timer = setTimeout(fetchInvoice, 1500);
    return () => clearTimeout(timer);
  }, [paymentId, orderId]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (invoiceUrl && invoiceUrl.startsWith('blob:')) {
        URL.revokeObjectURL(invoiceUrl);
      }
    };
  }, [invoiceUrl]);

  const downloadInvoice = () => {
    if (!invoiceUrl) return;
    const link = document.createElement('a');
    link.href = invoiceUrl;
    link.download = `Invoice_${orderId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const previewInvoice = () => {
    if (!invoiceUrl) return;
    setShowPreview(true);
  };

  // Don't render anything if invalid params (will redirect)
  if (!paymentId || !orderId) return null;

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4 bg-gray-50/50 dark:bg-dark-900/50">
      <div className="w-full max-w-[400px] opacity-0 animate-fade-in-up" style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}>
        <div className="card overflow-hidden shadow-2xl border-none">
          {/* Success Header Gradient */}
          <div 
            className="h-2 w-full"
            style={{ background: 'linear-gradient(90deg, #10b981, #3b82f6, #8b5cf6)' }}
          />
          
          <div className="p-4 text-center">
            {/* Success Icon with Pulse */}
            <div className="relative inline-block mb-4">
              <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping scale-150 opacity-20" />
              <div 
                className="relative w-16 h-16 rounded-full flex items-center justify-center mx-auto shadow-lg"
                style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
              >
                <FaCheckCircle className="w-8 h-8 text-white" />
              </div>
            </div>
            
            <h2 className="text-xl font-bold text-dark-800 dark:text-white mb-1 tracking-tight">Payment Successful!</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 font-medium">Thank you for your purchase. Your service is now being activated.</p>

            {/* Payment Details Card */}
            <div className="p-3 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 mb-4 text-left space-y-1 relative overflow-hidden group">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Order ID</span>
                <span className="text-xs font-mono font-bold text-dark-700 dark:text-gray-300 truncate ml-2">{orderId}</span>
              </div>
              <div className="h-px bg-gray-100 dark:bg-dark-600" />
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Payment ID</span>
                <span className="text-xs font-mono font-bold text-dark-700 dark:text-gray-300 truncate ml-2">{paymentId}</span>
              </div>
            </div>

            {/* Invoice Buttons - Show immediately, loading state for invoice */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <button 
                onClick={previewInvoice}
                disabled={!invoiceUrl}
                className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 border border-blue-100 dark:border-blue-800/30 transition-all duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-50"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  {invoiceLoading ? <FaSpinner className="w-5 h-5 animate-spin" /> : <FaEye className="w-5 h-5" />}
                </div>
                <span className="text-xs font-bold uppercase tracking-wider">Preview</span>
              </button>
              
              <button 
                onClick={downloadInvoice}
                disabled={!invoiceUrl}
                className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 border border-green-100 dark:border-green-800/30 transition-all duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-50"
              >
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                  {invoiceLoading ? <FaSpinner className="w-5 h-5 animate-spin" /> : <FaDownload className="w-5 h-5" />}
                </div>
                <span className="text-xs font-bold uppercase tracking-wider">Download</span>
              </button>
            </div>

            {/* Action Buttons - Always visible immediately */}
            <div className="space-y-3">
              <button
                onClick={() => navigate('/pricing')}
                className="w-full group relative overflow-hidden flex items-center justify-center gap-3 px-6 py-4 rounded-2xl text-white font-bold transition-all duration-300 hover:scale-[1.02] active:scale-95 shadow-xl hover:shadow-blue-500/25"
                style={{ 
                  background: `linear-gradient(135deg, #3b82f6, #2563eb)`,
                }}
              >
                <FaShoppingCart className="w-5 h-5" />
                <span>Buy More Services</span>
              </button>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => navigate('/Invoice')}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600 font-bold text-xs transition-all duration-300"
                >
                  <FaFileInvoice className="w-4 h-4" />
                  <span>All Invoices</span>
                </button>

                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600 font-bold text-xs transition-all duration-300"
                >
                  <FaHome className="w-4 h-4" />
                  <span>Dashboard</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Preview Modal - Rendered via Portal to escape stacking context */}
      {showPreview && invoiceUrl && createPortal(
        <div className="fixed inset-0 z-[9999] flex flex-col bg-white dark:bg-dark-900 animate-fade-in">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-500/10 rounded-lg text-primary-500">
                <FaFileInvoice className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-dark-800 dark:text-white leading-none">Invoice Preview</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Order ID: {orderId}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadInvoice}
                className="p-2.5 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-primary-500 hover:text-white transition-all duration-200"
                title="Download PDF"
              >
                <FaDownload className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2.5 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400 hover:bg-red-500 hover:text-white transition-all duration-200"
                title="Close Preview"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* PDF Viewer */}
          <div className="flex-1 bg-gray-100 dark:bg-dark-900 p-2 md:p-4 overflow-hidden">
            <div className="w-full h-full max-w-5xl mx-auto bg-white dark:bg-dark-800 rounded-xl shadow-2xl overflow-hidden border border-gray-200 dark:border-dark-700">
              <iframe
                src={`${invoiceUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                className="w-full h-full border-none"
                title="Invoice Preview"
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PaymentSuccess;
